import {
  collection,
  collectionGroup,
  doc,
  DocumentSnapshot,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable,
  type UploadTask,
} from "firebase/storage";
import type { User } from "firebase/auth";
import { db, storage } from "./firebaseClients";
import type { Clip, ClipDocument, ClipPage, ClipSort, UploadQueueItem } from "../types";

export const CLIPS_PER_PAGE = 20;

const sortConfig: Record<ClipSort, { field: "uploadedAt" | "recordingDate" | "bangerCount"; direction: "asc" | "desc" }> = {
  "uploaded-desc": { field: "uploadedAt", direction: "desc" },
  "uploaded-asc": { field: "uploadedAt", direction: "asc" },
  "recording-desc": { field: "recordingDate", direction: "desc" },
  "recording-asc": { field: "recordingDate", direction: "asc" },
  "bangers-desc": { field: "bangerCount", direction: "desc" },
};

function toDate(value: unknown): Date | null {
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.valueOf()) ? null : date;
  }
  return null;
}

export function mapClip(id: string, data: Record<string, unknown>): Clip {
  return {
    id,
    title: String(data.title ?? "Untitled clip"),
    description: data.description ? String(data.description) : undefined,
    recordingDate: toDate(data.recordingDate),
    uploadedAt: toDate(data.uploadedAt) ?? new Date(0),
    uploadedBy: String(data.uploadedBy ?? "Unknown"),
    uploaderName: String(data.uploaderName ?? "Player"),
    bangerCount: Number(data.bangerCount ?? 0),
    contentType: String(data.contentType ?? "video/mp4"),
    needsMetadataBackfill: !Object.prototype.hasOwnProperty.call(data, "uploaderName")
      || !Object.prototype.hasOwnProperty.call(data, "bangerCount"),
    durationSeconds: Number(data.durationSeconds ?? 0),
    sizeBytes: Number(data.sizeBytes ?? 0),
    storagePath: String(data.storagePath ?? ""),
    thumbnailPath: String(data.thumbnailPath ?? ""),
    videoUrl: String(data.videoUrl ?? ""),
    thumbnailUrl: String(data.thumbnailUrl ?? ""),
    visibility: "public",
  };
}

export async function backfillLegacyClipMetadata(clipId: string, uploaderName: string): Promise<void> {
  await updateDoc(doc(db, "clips", clipId), {
    uploaderName,
    bangerCount: 0,
  });
}

export async function getBangeredClipIds(userId: string, clipIds: string[]): Promise<Set<string>> {
  if (!clipIds.length) return new Set();
  const snapshot = await getDocs(query(
    collectionGroup(db, "bangers"),
    where("userId", "==", userId),
    where("clipId", "in", clipIds.slice(0, 30)),
  ));
  return new Set(snapshot.docs.map((item) => String(item.data().clipId)));
}

export async function markClipAsBanger(clipId: string, userId: string): Promise<boolean> {
  const clipReference = doc(db, "clips", clipId);
  const bangerReference = doc(db, "clips", clipId, "bangers", userId);

  return runTransaction(db, async (transaction) => {
    const [clipSnapshot, bangerSnapshot] = await Promise.all([
      transaction.get(clipReference),
      transaction.get(bangerReference),
    ]);
    if (!clipSnapshot.exists()) throw new Error("This clip is no longer available.");
    if (bangerSnapshot.exists()) return false;

    const currentCount = Number(clipSnapshot.data().bangerCount ?? 0);
    transaction.update(clipReference, { bangerCount: currentCount + 1 });
    transaction.set(bangerReference, {
      clipId,
      userId,
      createdAt: serverTimestamp(),
    });
    return true;
  });
}

function isMissingStorageObject(error: unknown): boolean {
  return typeof error === "object" && error !== null && "code" in error
    && (error as { code?: string }).code === "storage/object-not-found";
}

export async function deleteClipFully(clip: Clip): Promise<void> {
  const bangersSnapshot = await getDocs(collection(db, "clips", clip.id, "bangers"));
  const storageResults = await Promise.allSettled(
    [clip.storagePath, clip.thumbnailPath]
      .filter(Boolean)
      .map((path) => deleteObject(ref(storage, path))),
  );
  const storageFailure = storageResults.find(
    (result) => result.status === "rejected" && !isMissingStorageObject(result.reason),
  );
  if (storageFailure?.status === "rejected") throw storageFailure.reason;

  const bangerDocuments = bangersSnapshot.docs;
  for (let start = 0; start < bangerDocuments.length; start += 450) {
    const batch = writeBatch(db);
    bangerDocuments.slice(start, start + 450).forEach((item) => batch.delete(item.ref));
    await batch.commit();
  }

  const clipBatch = writeBatch(db);
  clipBatch.delete(doc(db, "clips", clip.id));
  await clipBatch.commit();
}

export async function getClipCount(): Promise<number> {
  const snapshot = await getCountFromServer(
    query(collection(db, "clips"), where("visibility", "==", "public")),
  );
  return snapshot.data().count;
}

export async function getTotalClipSize(): Promise<number> {
  const snapshot = await getDocs(
    query(collection(db, "clips"), where("visibility", "==", "public")),
  );
  return snapshot.docs.reduce((total, item) => total + Number(item.data().sizeBytes ?? 0), 0);
}

export async function getClipById(clipId: string): Promise<Clip | null> {
  const snapshot = await getDoc(doc(db, "clips", clipId));
  if (!snapshot.exists() || snapshot.data().visibility !== "public") return null;
  return mapClip(snapshot.id, snapshot.data());
}

export async function getClipPage(
  sort: ClipSort,
  cursor: DocumentSnapshot | null,
): Promise<ClipPage> {
  const { field, direction } = sortConfig[sort];
  const constraints = [
    where("visibility", "==", "public"),
    orderBy(field, direction),
    limit(CLIPS_PER_PAGE),
  ];
  const clipsQuery = cursor
    ? query(collection(db, "clips"), ...constraints, startAfter(cursor))
    : query(collection(db, "clips"), ...constraints);
  const snapshot = await getDocs(clipsQuery);
  const documents = snapshot.docs;

  return {
    clips: documents.map((item) => mapClip(item.id, item.data())),
    firstDocument: documents[0] ?? null,
    lastDocument: documents[documents.length - 1] ?? null,
    hasNextPage: documents.length === CLIPS_PER_PAGE,
  };
}

interface UploadCallbacks {
  onProgress: (progress: number, etaSeconds: number | null) => void;
}

function monitorUpload(
  task: UploadTask,
  progressStart: number,
  progressSpan: number,
  startedAt: number,
  callbacks: UploadCallbacks,
): Promise<void> {
  return new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => {
        const transferProgress = snapshot.totalBytes
          ? snapshot.bytesTransferred / snapshot.totalBytes
          : 0;
        const progress = progressStart + transferProgress * progressSpan;
        const elapsedSeconds = (Date.now() - startedAt) / 1000;
        const rate = progress > 0 ? progress / elapsedSeconds : 0;
        const etaSeconds = rate > 0 ? Math.max(0, (100 - progress) / rate) : null;
        callbacks.onProgress(Math.round(progress), etaSeconds);
      },
      reject,
      resolve,
    );
  });
}

export async function uploadClip(
  item: UploadQueueItem,
  user: User,
  callbacks: UploadCallbacks,
): Promise<string> {
  const clipReference = doc(collection(db, "clips"));
  const clipId = clipReference.id;
  const basePath = `clips/${user.uid}/${clipId}`;
  const videoPath = `${basePath}/video.mp4`;
  const thumbnailPath = `${basePath}/thumbnail.jpg`;
  const videoReference = ref(storage, videoPath);
  const thumbnailReference = ref(storage, thumbnailPath);
  const startedAt = Date.now();
  let videoUploaded = false;
  let thumbnailUploaded = false;

  try {
    const videoTask = uploadBytesResumable(videoReference, item.file, {
      contentType: item.file.type,
      customMetadata: { originalName: item.file.name, clipId },
    });
    await monitorUpload(videoTask, 0, 94, startedAt, callbacks);
    videoUploaded = true;

    const thumbnailTask = uploadBytesResumable(thumbnailReference, item.thumbnail, {
      contentType: "image/jpeg",
      cacheControl: "public,max-age=31536000,immutable",
    });
    await monitorUpload(thumbnailTask, 94, 5, startedAt, callbacks);
    thumbnailUploaded = true;

    const [videoUrl, thumbnailUrl] = await Promise.all([
      getDownloadURL(videoReference),
      getDownloadURL(thumbnailReference),
    ]);

    const clipData: Omit<ClipDocument, "uploadedAt"> & { uploadedAt: ReturnType<typeof serverTimestamp> } = {
      title: item.title.trim(),
      ...(item.description.trim() ? { description: item.description.trim() } : {}),
      recordingDate: item.recordingDate
        ? Timestamp.fromDate(new Date(`${item.recordingDate}T12:00:00`))
        : null,
      uploadedAt: serverTimestamp(),
      uploadedBy: user.uid,
      uploaderName: user.email?.split("@")[0] || user.displayName || "Player",
      bangerCount: 0,
      contentType: item.file.type || "video/mp4",
      durationSeconds: item.durationSeconds,
      sizeBytes: item.file.size,
      storagePath: videoPath,
      thumbnailPath,
      videoUrl,
      thumbnailUrl,
      visibility: "public",
    };

    await setDoc(clipReference, clipData);
    callbacks.onProgress(100, 0);
    return clipId;
  } catch (error) {
    await Promise.allSettled([
      videoUploaded ? deleteObject(videoReference) : Promise.resolve(),
      thumbnailUploaded ? deleteObject(thumbnailReference) : Promise.resolve(),
    ]);
    throw error;
  }
}
